import { AddIntegrationChannelDrawerFooter } from '@modules/integrationsV2/components/AddIntegrationChannelDrawer/AddIntegrationChannelDrawerFooter';
import { AddIntegrationChannelDrawerHeader } from '@modules/integrationsV2/components/AddIntegrationChannelDrawer/AddIntegrationChannelDrawerHeader';
import { IntegrationDescriptionForm } from '@modules/integrationsV2/components/IntegrationDescriptionForm';
import { SelectIntegrationWorkspace } from '@modules/integrationsV2/components/SelectIntegrationWorkspace';
import {
  useFetchSurveyMonkeySurveys,
  useUpdateIntegration,
  useValidateIntegrationForm,
} from '@modules/integrationsV2/hooks';
import { AddIntegrationChannelDrawerContentProps } from '@modules/integrationsV2/types';
import { getDescriptionMapInputForIntegrationChannel } from '@modules/integrationsV2/utils';
import { Autocomplete, Stack, TextField } from '@mui/material';
import {
  IntegrationChannelType,
  IntegrationDescriptionInput,
  SurveyInput,
} from 'generated/graphql';
import { useState } from 'react';
import { IsLoading } from 'v2/core/repository/edge-server/types';

export const AddSurveyMonkeyIntegrationChannelDrawerContent = ({
  onDrawerClose,
  integrationID,
  integrationType,
  integrationData,
  channelDescriptions,
}: AddIntegrationChannelDrawerContentProps) => {
  const [selectedSurvey, setSelectedSurvey] = useState<SurveyInput | null>(
    null,
  );
  const [selectedWorkspaceID, setSelectedWorkspaceID] = useState(integrationID);
  const [descriptionInput, setDescriptionInput] = useState('');

  const [updateIntegration, { status: updateIntegrationStatus }] =
    useUpdateIntegration({
      channelType: IntegrationChannelType.SurveyMonkey,
      integrationType,
    });

  const { data: surveys, status: surveyMonkeySurveysStatus } =
    useFetchSurveyMonkeySurveys(selectedWorkspaceID);

  const existingSurveys = integrationData.surveyMonkey?.includedSurveys ?? [];

  const [
    validateForm,
    {
      isDisabled,
      isDescriptionRequired,
      isValidatingDescription,
      descriptionValidForQuestions,
      validatedDescriptionMessage,
      isPreValidatedDescription,
    },
  ] = useValidateIntegrationForm({
    channelType: IntegrationChannelType.SurveyMonkey,
    integrationType,
    hasValidFormFields: !!selectedSurvey,
    descriptionInput,
    currentDescription: '',
  });

  const integrateSurveyMonkeySurvey = async (
    selectedSurvey: SurveyInput | null,
    addAnother = false,
  ) => {
    const isValid = await validateForm();

    if (!isValid || !selectedSurvey) {
      return;
    }

    let updateDesciptionInput: IntegrationDescriptionInput | undefined;

    if (descriptionInput) {
      updateDesciptionInput = {
        descriptionMap: getDescriptionMapInputForIntegrationChannel(
          channelDescriptions,
          {
            description: descriptionInput.trim(),
            sourceID: selectedSurvey.surveyID,
          },
        ),
      };
    }

    // Remove duplicates
    const includedSurveys = [
      ...new Map(
        [...existingSurveys, selectedSurvey].map((survey) => [
          survey.surveyID,
          survey,
        ]),
      ).values(),
    ];

    const { errors } = await updateIntegration({
      integrationID: selectedWorkspaceID,
      isActive: true,
      updateData: {
        surveyMonkey: {
          includedSurveys,
        },
      },
      updateDescription: updateDesciptionInput,
    });

    if (!!errors?.length) {
      return;
    }

    if (!addAnother) {
      onDrawerClose();
      return;
    }

    setSelectedSurvey(null);
    setDescriptionInput('');
  };

  const addedSurveyIDsSet = new Set(existingSurveys.map((s) => s.surveyID));

  const options = surveys.filter(
    (survey) => !addedSurveyIDsSet.has(survey.surveyID),
  );

  return (
    <Stack height="100%" justifyContent="space-between">
      <Stack>
        <AddIntegrationChannelDrawerHeader title="Integrate a SurveyMonkey Survey" />
        <Stack spacing={4} p={3}>
          <SelectIntegrationWorkspace
            selectedWorkspaceID={selectedWorkspaceID}
            onChange={(workspaceID) => setSelectedWorkspaceID(workspaceID)}
            channelType={IntegrationChannelType.SurveyMonkey}
            integrationType={integrationType}
          />
          <Autocomplete
            fullWidth
            multiple={false}
            value={selectedSurvey}
            options={options}
            getOptionLabel={(option) => option?.name}
            onChange={(_, selected) => selected && setSelectedSurvey(selected)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select SurveyMonkey Survey"
                required
              />
            )}
            componentsProps={{ paper: { elevation: 8 } }}
            loading={IsLoading(surveyMonkeySurveysStatus)}
            isOptionEqualToValue={(option, value) =>
              option.surveyID === value?.surveyID
            }
            clearIcon={null}
          />
          <IntegrationDescriptionForm
            value={descriptionInput}
            onChange={setDescriptionInput}
            isRequired={isDescriptionRequired}
            validatedDescriptionMessage={validatedDescriptionMessage}
            descriptionValidForQuestions={descriptionValidForQuestions}
            isValidationInProgress={isValidatingDescription}
            isPreValidatedDescription={isPreValidatedDescription}
            integrationType={integrationType}
          />
        </Stack>
      </Stack>
      <AddIntegrationChannelDrawerFooter
        onDrawerClose={onDrawerClose}
        onIntegrateAndAddAnother={() =>
          integrateSurveyMonkeySurvey(selectedSurvey, true)
        }
        onIntegrate={() => integrateSurveyMonkeySurvey(selectedSurvey)}
        isIntegrating={IsLoading(updateIntegrationStatus)}
        isDisabled={isDisabled}
        shouldOpenCancelConfirmationDialog={
          !!selectedSurvey || !!descriptionInput
        }
      />
    </Stack>
  );
};
